import { api } from '@admin/services/api';
import type {
  CreateQuestionRequest,
  Difficulty,
  QuestionAssistRequest,
  QuestionAssistResponse,
  QuestionListItem,
  QuestionReviewResponse,
  QuestionType,
  QuestionsListResponse,
} from '@games/word-clash/shared/types';

/**
 * Admin questions API client. Wraps the REST endpoints in
 * `games/word-clash/server/admin/questionsRoutes.ts` (paths unchanged, #817).
 *
 * Server support matrix (as of #289):
 *   - GET    /admin/questions          → paginated list (filters: type, difficulty, isActive)
 *   - POST   /admin/questions          → create
 *   - PATCH  /admin/questions/:id      → partial update
 *   - GET    /admin/questions/:id      → NOT IMPLEMENTED. We emulate it by
 *                                        calling list with a large pageSize
 *                                        and finding the matching row. Good
 *                                        enough until #289 follow-up adds
 *                                        a dedicated detail endpoint.
 *   - DELETE /admin/questions/:id      → NOT IMPLEMENTED. The admin UI uses
 *                                        `isActive=false` as a soft-delete
 *                                        affordance via `toggleQuestionActive`.
 */

export interface ListQuestionsParams {
  page?: number;
  pageSize?: number;
  type?: QuestionType | '';
  difficulty?: Difficulty | '';
  isActive?: boolean | '';
  /**
   * `true` → reviewed-only. `false` → unreviewed queue (the Review page).
   * Empty string / undefined = no filter.
   */
  reviewed?: boolean | '';
  /**
   * Filter on `Question.category`. Accepts a single slug string or an array
   * of slugs for multi-select. Empty string / empty array = no filter; the
   * wire shape is a comma-joined string to keep the URL simple.
   */
  category?: string | readonly string[];
  /** Presence filters — `true` keeps rows that have ≥1 entry; `false` keeps rows that are missing or empty. */
  hasValidAnswersTr?: boolean | '';
  hasSpecialAnswers?: boolean | '';
  hasSpecialAnswersTr?: boolean | '';
  hasPlausibleWrongAnswers?: boolean | '';
  hasAliases?: boolean | '';
  hasAliasesTr?: boolean | '';
}

/** Build a query string, skipping empty / undefined values. */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listQuestions(
  params: ListQuestionsParams = {},
): Promise<QuestionsListResponse> {
  const normBool = (v: boolean | '' | undefined) =>
    v === '' || v === undefined ? undefined : v;
  // Collapse single-item arrays into a single slug to keep the URL short.
  const categoryParam = Array.isArray(params.category)
    ? params.category.length > 0
      ? params.category.join(',')
      : undefined
    : params.category || undefined;
  const qs = toQuery({
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    type: params.type,
    difficulty: params.difficulty,
    isActive: normBool(params.isActive),
    reviewed: normBool(params.reviewed),
    category: categoryParam,
    hasValidAnswersTr: normBool(params.hasValidAnswersTr),
    hasSpecialAnswers: normBool(params.hasSpecialAnswers),
    hasSpecialAnswersTr: normBool(params.hasSpecialAnswersTr),
    hasPlausibleWrongAnswers: normBool(params.hasPlausibleWrongAnswers),
    hasAliases: normBool(params.hasAliases),
    hasAliasesTr: normBool(params.hasAliasesTr),
  });
  return api<QuestionsListResponse>(`/admin/questions${qs}`);
}

/**
 * Fetch EVERY question matching the given server-side filters, paging through
 * the list endpoint until the server reports no more rows. Used by the list
 * screen's search box so the client-side text filter can match across the
 * whole bank instead of only the page currently on screen. The bank is small
 * (a few hundred rows) so eagerly loading it is cheap; `MAX_PAGES` is a
 * safety valve against an unexpectedly large result set / a server that never
 * clears `hasMore`.
 */
export async function fetchAllQuestions(
  params: Omit<ListQuestionsParams, 'page' | 'pageSize'> = {},
): Promise<QuestionListItem[]> {
  const PAGE_SIZE = 100; // server caps pageSize at 100
  const MAX_PAGES = 100; // hard stop: 10k rows
  const all: QuestionListItem[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await listQuestions({ ...params, page, pageSize: PAGE_SIZE });
    all.push(...res.data);
    if (!res.hasMore || res.data.length === 0) break;
  }
  return all;
}

/**
 * Fetch a single question by id via the dedicated GET endpoint.
 * Returns the same `QuestionListItem` shape as the list endpoint so the
 * Edit screen can drive the form straight from the response.
 */
export async function getQuestion(id: string): Promise<QuestionListItem> {
  return api<QuestionListItem>(`/admin/questions/${encodeURIComponent(id)}`);
}

export async function createQuestion(
  body: CreateQuestionRequest,
): Promise<QuestionListItem> {
  return api<QuestionListItem>('/admin/questions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateQuestion(
  id: string,
  body: Partial<CreateQuestionRequest> & { isActive?: boolean },
): Promise<QuestionListItem> {
  return api<QuestionListItem>(`/admin/questions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Soft-delete affordance — there is no DELETE endpoint on the server. Flips
 * the `isActive` flag via the standard PATCH handler.
 */
export async function toggleQuestionActive(
  id: string,
  isActive: boolean,
): Promise<QuestionListItem> {
  return updateQuestion(id, { isActive });
}

/**
 * Mark a question as reviewed (approve). Idempotent on the server.
 */
export async function approveQuestion(
  id: string,
): Promise<QuestionReviewResponse> {
  return api<QuestionReviewResponse>(
    `/admin/questions/${encodeURIComponent(id)}/approve`,
    { method: 'POST' },
  );
}

/**
 * Reject a question — marks reviewed AND flips isActive=false so it leaves
 * the in-game pool. Idempotent on the server.
 */
export async function rejectQuestion(
  id: string,
): Promise<QuestionReviewResponse> {
  return api<QuestionReviewResponse>(
    `/admin/questions/${encodeURIComponent(id)}/reject`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// AI authoring assistant
// ---------------------------------------------------------------------------

/**
 * POST /admin/questions/assist — ask Claude (subscription-backed) to propose
 * improved question content. Returns a chat reply + an optional patch the
 * caller applies into the form. Never persists — saving still goes through
 * createQuestion / updateQuestion.
 */
export async function assistQuestion(
  body: QuestionAssistRequest,
  signal?: AbortSignal,
): Promise<QuestionAssistResponse> {
  return api<QuestionAssistResponse>('/admin/questions/assist', {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  });
}

// ---------------------------------------------------------------------------
// Quality dashboard (B-2.6)
// ---------------------------------------------------------------------------

export interface ListQuestionQualityParams {
  page?: number;
  pageSize?: number;
  language?: 'en' | 'tr';
  category?: string;
}

/**
 * GET /admin/questions/quality — paginated, sorted by funScore ASC. Worst-
 * rated rows surface first so reviewers can fix systematically. Mirrors the
 * standard `QuestionsListResponse` envelope.
 */
export async function listQuestionQuality(
  params: ListQuestionQualityParams = {},
): Promise<QuestionsListResponse> {
  const qs = toQuery({
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    language: params.language,
    category: params.category,
  });
  return api<QuestionsListResponse>(`/admin/questions/quality${qs}`);
}
